import { useMemo, useEffect, memo, useState } from 'react';
import Bluebird from 'bluebird';
import { capitalize } from 'lodash';
import { Table, TBody, THead, TH, TableRow } from '@/components/DesignLibrary/Table';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import Filters from '@/components/DesignLibrary/Filters';
import Header from '@/components/DesignLibrary/Header';
import ProgressBarLoader from '@/components/ProgressBarLoader';
import Note from '@components/Icons/Note';
import AddUserWorkflowNoteModal from '@modals/AddUserWorkflowNoteModal';
import Head from 'next/head';
import { IDENTIFIERS, getUrl } from '@/helpers/urlsHelper';

import ItemRow from './ItemRow';
import { columns, FILTERS } from './constants';

const MemoizedItemRow = memo(ItemRow);

const UserWorkflowActivityThread: React.FC<{ userWorkflowId: string, showOnly?: Exclude<typeof FILTERS[number]['value'], 'all'> }> = ({ userWorkflowId, showOnly }) => {
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<typeof FILTERS[number]['value']>(showOnly || 'all');

  const { data, fetchingState, getData: getUserWorkflowActivityById } = useNoodleApi(tsClient.workflows.getUserWorkflowActivityById);

  const { data: userWorkflowData, getData: getUserWorkflowFn } = useNoodleApi(tsClient.workflows.getUserWorkflowById, { toastOnError: true });

  const { data: collaborators, getData: getCollaboratorsFn } = useNoodleApi(tsClient.workflows.getUserWorkflowCollaborators, { toastOnError: true });

  useEffect(() => {
    if (userWorkflowId) {
      getUserWorkflowFn({
        userWorkflowId,
      });
      getCollaboratorsFn({ userWorkflowId });
    }
  }, [userWorkflowId, getUserWorkflowFn, getCollaboratorsFn]);

  useEffect(() => {
    if (userWorkflowId) {
      getUserWorkflowActivityById({
        filter: selectedFilter,
        page: 0,
        perPage: 1000,
        userWorkflowId,
      });
    }
  }, [userWorkflowId, getUserWorkflowActivityById, selectedFilter]);

  const widgetUserWorkflowActivityItems = useMemo(
    () =>
      data
        ? data.items.map(({ action, activityType, details, createdAt, personName, personId, id, type }) => ({
          action: {
            action,
            activityType,
            type,
          },
          details,
          id,
          time: createdAt,
          user: {
            id: personId,
            name: personName,
          },
        }))
        : [],
    [data],
  );

  if (!userWorkflowData || (!widgetUserWorkflowActivityItems && fetchingState.isFetching)) {
    return (
      <ProgressBarLoader
        chunks={[
          {
            label: "Fetching case activity...",
            loaded: fetchingState.isFetched,
          },
          {
            label: "Fetching case data...",
            loaded: Boolean(userWorkflowData),
          },
        ]}
      />
    );
  }

  const showOnlyTitle = FILTERS.find(f => f.value === showOnly)?.title;

  return (
    <>
      <Head>
        <title>Case {showOnlyTitle ? `${showOnlyTitle}s` : 'Activity'} · Glade Dashboard</title>
      </Head>
      <Header
        breadcrumbs={showOnly
          ? [
            {
              href: getUrl(IDENTIFIERS.DASHBOARD_USER_WORKFLOW_DETAIL, { userWorkflowId }),
              label: 'Go back to workflow',
            },
          ]
          : []}
        title={`Case ${showOnlyTitle ? `${showOnlyTitle}s` : 'Activity'}`}
        description={`${userWorkflowData.person.name} · ${userWorkflowData.workflow.name}`}
        actions={[
          {
            icon: Note,
            label: 'Add case note',
            onClick: () => setIsNoteModalOpen(true),
          },
        ]}
      />
      {!showOnly && <Filters
        items={[
          {
            isMultiSelect: false,
            items: FILTERS.map((filter) => ({
              key: filter.value,
              label: filter.title,
            })),
            label: 'Type',
            onChange: (filter) => setSelectedFilter(filter as typeof FILTERS[number]['value']),
            selected: selectedFilter || 'all',
          },
        ]}
      />}
      <Table>
        <THead>
          <TableRow>
            {columns.map(c => (
              <TH key={c} label={capitalize(c)} />
            ))}
          </TableRow>
        </THead>
        <TBody>
          {widgetUserWorkflowActivityItems.map(item => (
            <MemoizedItemRow key={item.id} item={item} />
          ))}
        </TBody>
      </Table>
      {isNoteModalOpen && (
        <AddUserWorkflowNoteModal
          collaborators={collaborators || []}
          userWorkflowId={userWorkflowId}
          onClose={() => setIsNoteModalOpen(false)}
          onAddComment={async () => {
            // Need to delay, since activity is added on event listener
            await Bluebird.delay(250);
            await getUserWorkflowActivityById({
              filter: selectedFilter,
              page: 0,
              perPage: 1000,
              userWorkflowId,
            });
          }}
        />
      )}
    </>
  );
};

export default UserWorkflowActivityThread;
